import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, CreditCard, Calendar,
  AlertTriangle, CheckCircle2, XCircle, Crown, Shield
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import { PLAN_PRICES } from '@/lib/plans';
import AppShell from '@/components/AppShell';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const { user, getAuthHeader, refreshUser, subscriptionStatus } = useAuth();
  const { formatDate: regionFormatDate, formatPrice } = useRegion();
  const navigate = useNavigate();
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/payments/subscription-info`, {
        headers: getAuthHeader()
      });
      setSubInfo(res.data);
    } catch (err) {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(
        `${API}/api/payments/cancel-subscription`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setShowConfirm(false);
      await refreshUser();
      await fetchSubscriptionInfo();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return regionFormatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const planLabel = (plan) => {
    if (plan === 'pro') return 'Pro';
    if (plan === 'basic') return 'Basic';
    return plan || '—';
  };

  const planPriceDisplay = (plan) => {
    if (plan === 'pro') return formatPrice(PLAN_PRICES.pro);
    if (plan === 'basic') return formatPrice(PLAN_PRICES.basic);
    return subInfo?.amount ? formatPrice(subInfo.amount) : '—';
  };

  const statusBadge = () => {
    if (!subInfo?.has_subscription) return null;
    const s = subInfo.status;
    if (subInfo.cancel_at_period_end) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-sm font-medium" data-testid="sub-status-cancelling">
          <SEO title="Subscription" description="Manage your Poettik subscription, view your plan and renewal date." noindex />
          <AlertTriangle className="w-3.5 h-3.5" /> Cancelling
        </span>
      );
    }
    if (s === 'active') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-sm font-medium" data-testid="sub-status-active">
          <CheckCircle2 className="w-3.5 h-3.5" /> Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 text-red-400 text-sm font-medium" data-testid="sub-status-inactive">
        <XCircle className="w-3.5 h-3.5" /> {s || 'Inactive'}
      </span>
    );
  };

  if (loading) {
    return (
      <AppShell title="Subscription">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#9D174D]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Subscription">
      <SEO title="Manage subscription" description="Manage your Poettik plan." noindex />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8 text-center">
          Manage Subscription
        </h1>

        {!subInfo?.has_subscription ? (
          /* No subscription */
          <Card variant="glass" className="p-8 text-center" data-testid="no-subscription-card">
            <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold mb-2">No Active Subscription</h2>
            <p className="text-muted-foreground mb-6">
              {subscriptionStatus?.subscription_type === 'trial'
                ? `You're on a free trial. Upgrade to unlock all features.`
                : `You don't have an active subscription yet.`}
            </p>
            <Link to="/pricing">
              <Button variant="premium" size="lg" data-testid="upgrade-btn">
                <Crown className="w-5 h-5 mr-2" /> View Plans
              </Button>
            </Link>
          </Card>
        ) : (
          /* Active subscription details */
          <Card variant="glass" className="overflow-hidden" data-testid="subscription-details-card">
            {/* Plan header */}
            <div className="p-6 bg-gradient-to-r from-primary/10 to-accent/10 border-b border-border/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  {subInfo.plan === 'pro' ? (
                    <Crown className="w-5 h-5 text-primary" />
                  ) : (
                    <Shield className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold" data-testid="sub-plan-name">
                    {planLabel(subInfo.plan)} Plan
                  </h2>
                  <p className="text-sm text-muted-foreground">Monthly subscription</p>
                </div>
              </div>
              {statusBadge()}
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Details grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-background/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Monthly Price</p>
                  <p className="text-xl font-bold" data-testid="sub-price">{planPriceDisplay(subInfo.plan)}</p>
                </div>
                <div className="p-4 rounded-lg bg-background/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    {subInfo.cancel_at_period_end ? 'Access Until' : 'Next Renewal'}
                  </p>
                  <p className="text-xl font-bold" data-testid="sub-renewal-date">
                    {formatDate(subInfo.current_period_end)}
                  </p>
                </div>
              </div>

              {/* Cancel / Already cancelling */}
              {subInfo.cancel_at_period_end ? (
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center" data-testid="cancellation-notice">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-amber-200">
                    Your subscription is set to cancel. You'll have full access until{' '}
                    <strong>{formatDate(subInfo.current_period_end)}</strong>.
                  </p>
                </div>
              ) : (
                <>
                  {!showConfirm ? (
                    <Button
                      variant="outline"
                      className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      onClick={() => setShowConfirm(true)}
                      data-testid="cancel-sub-btn"
                    >
                      Cancel Subscription
                    </Button>
                  ) : (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 space-y-4" data-testid="cancel-confirm-box">
                      <p className="text-sm text-center">
                        Are you sure? You'll keep access until the end of your billing period.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => setShowConfirm(false)}
                          data-testid="cancel-keep-btn"
                        >
                          Keep Subscription
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={handleCancel}
                          disabled={cancelling}
                          data-testid="cancel-confirm-btn"
                        >
                          {cancelling ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : null}
                          Yes, Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </AppShell>
  );
};

export default SubscriptionPage;
